import React, { useState, useEffect, useCallback } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { useCart } from './CartContext';

const CheckoutStatus = {
  IDLE: 'idle',
  LOADING: 'loading',
  PROCESSING: 'processing',
  SUCCESS: 'success',
  ERROR: 'error'
};

const CheckoutForm = ({
  registration,
  campId,
  csrfToken,
  onSuccessfulCheckout,
  onError
}) => {
  // Core state
  const [status, setStatus] = useState(CheckoutStatus.IDLE);
  const [errorMessage, setErrorMessage] = useState('');
  const [discountCode, setDiscountCode] = useState('');

  // Stripe setup
  const stripe = useStripe();
  const elements = useElements();

  // Cart context
  const { payment, refreshCart } = useCart();
  const { clientSecret, intentId: paymentIntentId } = payment;

  // Initialize checkout by adding registration to cart
  useEffect(() => {
    const initializeCheckout = async () => {
      try {
        setStatus(CheckoutStatus.LOADING);
        setErrorMessage('');

        const response = await fetch(`/camps/${campId}/registrations`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken
          },
          body: JSON.stringify({
            registration: {
              ...registration,
              camp_id: campId,
              price_point_id: registration.price_point_id
            }
          })
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to add registration to cart');
        }

        // Refresh cart to include new registration
        await refreshCart();
        setStatus(CheckoutStatus.IDLE);

      } catch (error) {
        setStatus(CheckoutStatus.ERROR);
        setErrorMessage(error.message);
        if (onError) onError(error);
      }
    };

    initializeCheckout();
  }, [campId, registration, refreshCart, csrfToken, onError]);

  // Apply discount code
  const applyDiscount = async () => {
    if (!discountCode.trim()) return;

    try {
      const response = await fetch('/apply_discount', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ 
          code: discountCode,
          payment_intent_id: paymentIntentId 
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to apply discount code');
      }

      // Refresh cart after discount application
      await refreshCart();

    } catch (error) {
      setErrorMessage('Failed to apply discount: ' + error.message);
      throw error;
    }
  };

  // Handle form submission and payment confirmation
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || status === CheckoutStatus.PROCESSING || !clientSecret) {
      return;
    }

    try {
      setStatus(CheckoutStatus.PROCESSING);
      setErrorMessage('');

      // Apply discount if provided
      if (discountCode) {
        await applyDiscount();
      }

      // Ensure payment intent is up to date with final cart state
      await fetch('/update_checkout', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ payment_intent_id: paymentIntentId })
      });

      // Confirm payment with Stripe
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/orders/success?payment_intent=${paymentIntentId}`,
        },
      });

      if (result.error) {
        throw result.error;
      }

      setStatus(CheckoutStatus.SUCCESS);
      if (onSuccessfulCheckout) {
        onSuccessfulCheckout();
      }

    } catch (error) {
      console.error('Payment error:', error);
      setStatus(CheckoutStatus.ERROR);
      setErrorMessage(error.message);
      if (onError) onError(error);
    }
  };

  // Render loading state
  if (status === CheckoutStatus.LOADING) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <span className="ml-2">Setting up checkout...</span>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto p-4">
      <form id="payment-form" onSubmit={handleSubmit} className="space-y-4">
        <fieldset disabled={status === CheckoutStatus.PROCESSING || !stripe || !elements} 
                  className="space-y-4">
          {/* Discount Code Input */}
          <div className="space-y-2">
            <label htmlFor="discount-code" className="block text-sm font-medium">
              Discount Code:
            </label>
            <div className="flex gap-2">
              <input
                id="discount-code"
                type="text"
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value)}
                placeholder="Enter discount code (optional)"
                className="flex-1 p-2 border rounded focus:ring-2 focus:ring-blue-500"
                disabled={status === CheckoutStatus.PROCESSING}
              />
              <button
                type="button"
                onClick={applyDiscount}
                disabled={!discountCode.trim() || status === CheckoutStatus.PROCESSING}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 
                         disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply
              </button>
            </div>
          </div>

          {/* Payment Element */}
          {clientSecret && (
            <div className="space-y-4">
              <PaymentElement id="payment-element" />
              
              <button
                type="submit"
                disabled={status === CheckoutStatus.PROCESSING || !stripe}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 
                         disabled:opacity-50 disabled:cursor-not-allowed
                         transition-colors duration-200"
              >
                {status === CheckoutStatus.PROCESSING ? (
                  <span className="flex items-center justify-center">
                    <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                    Processing...
                  </span>
                ) : (
                  'Pay Now'
                )}
              </button>
            </div>
          )}

          {/* Error Messages */}
          {errorMessage && (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}
        </fieldset>
      </form>
    </div>
  );
};

export default CheckoutForm;